"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "./cn";

/**
 * Port of the reference's Dialog (src/components/ui/Dialog.tsx). Compound
 * component (Dialog/DialogTrigger/DialogContent/DialogTitle/
 * DialogDescription/DialogClose) with its own focus trap, Escape handling,
 * body scroll lock, and portal — no @radix-ui/react-dialog: see Button.tsx
 * for why (radix hoists to the repo-root node_modules and resolves the Vite
 * SPA's react 18 under vitest). No reference test for Dialog itself;
 * Dialog.test.tsx below is authored.
 */

interface DialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titleId: string;
  descriptionId: string;
}

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("Dialog compound components must be used within <Dialog>");
  return ctx;
}

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Dialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOpen = false,
  children,
}: DialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const id = useId();

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      controlledOnOpenChange?.(next);
    },
    [isControlled, controlledOnOpenChange],
  );

  return (
    <DialogContext.Provider
      value={{
        open,
        onOpenChange,
        titleId: `${id}-title`,
        descriptionId: `${id}-desc`,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
}

interface DialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

function DialogTrigger({ onClick, children, ...props }: DialogTriggerProps) {
  const { onOpenChange } = useDialogContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    onOpenChange(true);
  };

  return (
    <button type="button" onClick={handleClick} {...props}>
      {children}
    </button>
  );
}

interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  inline?: boolean;
  overlayClassName?: string;
  containerClassName?: string;
}

function DialogContent({
  className,
  children,
  inline = false,
  overlayClassName,
  containerClassName,
  ...props
}: DialogContentProps) {
  const { open, onOpenChange, titleId, descriptionId } = useDialogContext();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus trap and restore
  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const timer = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    });

    return () => {
      cancelAnimationFrame(timer);
      previousFocusRef.current?.focus();
    };
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onOpenChange(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Lock body scroll
  useEffect(() => {
    if (inline) return;
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [inline, open]);

  if (!open) return null;

  // No aria-hidden on the outer container — it would remove the
  // role="dialog" panel inside from the accessibility tree entirely. Only
  // the overlay is decorative.
  const content = (
    <div
      className={cn(
        inline
          ? "absolute inset-0 z-50 flex items-end justify-center p-0 md:items-center md:p-4"
          : "fixed inset-0 z-50 flex items-end justify-center p-0 md:items-center md:p-4",
        containerClassName,
      )}
    >
      {/* Overlay */}
      <div
        className={cn(
          inline
            ? "absolute inset-0 bg-black/50 animate-exm-fade-in"
            : "fixed inset-0 bg-black/50 animate-exm-fade-in",
          overlayClassName,
        )}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Panel — bottom sheet on mobile, centered card at md+. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- the panel's stopPropagation-only onClick offers no interaction (see its comment); known jsx-a11y false positive on modal panels */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={cn(
          "relative z-50 w-full max-w-lg ds-card rounded-t-[28px] rounded-b-none md:rounded-[0.75rem] p-6 shadow-xl animate-exm-slide-up",
          "max-h-[85dvh] overflow-y-auto overscroll-contain pb-safe-bar",
          "md:max-h-[calc(100dvh-4rem)] md:pb-6",
          className,
        )}
        // The panel swallows clicks so an ancestor's handler cannot treat
        // clicks INSIDE the dialog as outside activity — load-bearing in
        // inline mode, which renders in place rather than in a portal.
        // stopPropagation-only: no interaction is offered, so there is no
        // keyboard equivalent to add. Disable directive sits above the
        // element: jsx-a11y reports at the element, not the attribute.
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        <div
          aria-hidden="true"
          className="mx-auto -mt-2 mb-3.5 h-1 w-10 rounded-full bg-edge md:hidden"
        />
        {children}
      </div>
    </div>
  );

  if (inline || typeof document === "undefined") {
    return content;
  }

  return createPortal(content, document.body);
}

interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

function DialogTitle({ className, children, ...props }: DialogTitleProps) {
  const { titleId } = useDialogContext();
  return (
    <h2 id={titleId} className={cn("text-lg font-semibold text-ink", className)} {...props}>
      {children}
    </h2>
  );
}

interface DialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

function DialogDescription({ className, children, ...props }: DialogDescriptionProps) {
  const { descriptionId } = useDialogContext();
  return (
    <p id={descriptionId} className={cn("mt-2 text-sm text-ink-light", className)} {...props}>
      {children}
    </p>
  );
}

type DialogCloseProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

function DialogClose({ onClick, children, ...props }: DialogCloseProps) {
  const { onOpenChange } = useDialogContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    onOpenChange(false);
  };

  return (
    <button type="button" onClick={handleClick} {...props}>
      {children ?? <span className="sr-only">Close</span>}
    </button>
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
export type {
  DialogProps,
  DialogTriggerProps,
  DialogContentProps,
  DialogTitleProps,
  DialogDescriptionProps,
  DialogCloseProps,
};
